/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
import * as React from 'react';
import cls from 'classnames';

import animateHeight from 'src/animations/animateHeight';
import { useNonInitialLayoutEffect } from 'src/explore/hooks/useNonInitialEffect';
import BreakpointsService from 'src/explore/services/breakpoints';

export type IHeightByBreakpoint = {
  xs?: number | null;
  sm?: number | null;
  md?: number | null;
  lg?: number | null;
};

export interface ExpandProps {
  className?: string;
  initialHeight?: number | IHeightByBreakpoint;
  isOpen: boolean;
  children:
    | React.ReactNode
    | (( props: {
        collapsedHeight?: number | null;
        isOpen?: boolean;
        overflow?: boolean;
      }) => React.ReactNode );
}

export const Expand = ({ children, className, isOpen, initialHeight }: ExpandProps ) => {
  const [ overflow, setOverflow ] = React.useState( false );

  // initial height for every breakpoint
  const initialHeightByBreakpoint = useInitialHeightByBreakpoint( initialHeight );

  // compute collpased height for this breakpoint
  const collapsedHeight = useCollapsedHeight( initialHeightByBreakpoint );

  // set refs
  const containerRef = React.useRef<HTMLDivElement>( null );
  const [ contentRef, setContentRef, height ] = useRefCallback();

  // expansion logic
  useAnimateHeight( containerRef.current, contentRef, !isOpen, collapsedHeight );

  // compute overflow
  React.useEffect(() => {
    const determineOverflow = () => {
      if ( contentRef ) {
        setOverflow(
          typeof collapsedHeight === 'number' && collapsedHeight < calculateHeight( contentRef )
        );
      }
    };

    determineOverflow();
    window.addEventListener( 'resize', determineOverflow );
    return () => {
      window.removeEventListener( 'resize', determineOverflow );
    };
  }, [ collapsedHeight, contentRef, height ]);

  return (
    <div
      className={cls( 'overflow-hidden', className )}
      ref={containerRef}
      style={overflow && !isOpen ? { height: collapsedHeight } : null}
    >
      <div className="overflow-hidden" ref={setContentRef}>
        {typeof children === 'function'
          ? children({ collapsedHeight, isOpen, overflow })
          : children}
      </div>
    </div>
  );
};

const calculateHeight = ( elem: HTMLDivElement ) => {
  const style = getComputedStyle( elem );
  return elem.offsetHeight + parseInt( style.marginTop, 10 ) + parseInt( style.marginBottom, 10 );
};

/**
 * HOOKS
 */

// hook to animate height bsased on open state
function useAnimateHeight(
  container: HTMLDivElement,
  content: HTMLDivElement,
  open: boolean,
  minHeight: number
) {
  // animation function
  const setHeight = React.useCallback(
    () =>
      animateHeight({
        container,
        content,
        duration: 200,
      }),
    [ container, content ]
  );

  // run animation anytime open state changes
  useNonInitialLayoutEffect(() => {
    setHeight()({
      open,
      minHeight,
    });
  }, [ open ]);
}

// compute collapsed height for current breakpoint
function useCollapsedHeight( initialHeights: IHeightByBreakpoint ) {
  const [ collapsedHeight, setCollapsedHeight ] = React.useState<number>( null );

  React.useEffect(() => {
    const collapse = () => {
      // initial height for current breakpoint
      const newHeight =
        initialHeights[BreakpointsService.getBreakpointSize() as keyof IHeightByBreakpoint];
      setCollapsedHeight( newHeight );
    };
    collapse();

    window.addEventListener( 'resize', collapse );
    return () => {
      window.removeEventListener( 'resize', collapse );
    };
  }, [ initialHeights ]);

  return collapsedHeight;
}

// initial height for every breakpoint
export function useInitialHeightByBreakpoint( initialHeight: ExpandProps['initialHeight']) {
  return React.useMemo<IHeightByBreakpoint>(() => {
    if ( typeof initialHeight !== 'object' ) {
      return {
        xs: initialHeight,
        sm: initialHeight,
        md: initialHeight,
        lg: initialHeight,
      };
    }

    let cascadingHeight: number = initialHeight.xs || null;
    const heights: IHeightByBreakpoint = {};

    const sizes: Array<keyof IHeightByBreakpoint> = [ 'xs', 'sm', 'md', 'lg' ];
    sizes.forEach(( size: keyof IHeightByBreakpoint ) => {
      if ( initialHeight[size] || initialHeight[size] === null ) {
        cascadingHeight = initialHeight[size];
      }
      heights[size] = cascadingHeight;
    });

    return heights;
  }, [ initialHeight ]);
}

// ref function with callback to determine height
// any time the elem changes make sure to compute the height
function useRefCallback(): [HTMLDivElement | null, ( elem: HTMLDivElement ) => void, number | null] {
  const [ ref, setRef ] = React.useState<HTMLDivElement>( null );
  const [ height, setHeight ] = React.useState<number>( null );

  const refCallback = ( elem: HTMLDivElement ) => {
    if ( elem ) {
      setRef( elem );
      setHeight( calculateHeight( elem ));
    }
  };

  return [ ref, refCallback, height ];
}
